/**
 * Moving, resizing and turning the selection: shown at once and to everyone while it lasts
 * (`project.drag_boxes`), ended by one batch (one undo) or put back.
 */
import { unwrap } from "solid-js/store";
import { SNAP_PX } from "./constants";
import type { BoardCtx } from "./context";
import { angleTo, bounds, carried, resized, rounded, snap } from "./geometry";
import type { BoardPart } from "./types";

/** The gestures' members of the board service. */
export function movesPart(ctx: BoardCtx): BoardPart<"guides" | "beginMove" | "moveBy" | "beginResize" | "resizeBy" | "beginRotate" | "rotateTowards" | "end" | "cancel" | "busy"> {
  const { graph, project } = ctx.deps;
  return {
    guides: ctx.guides,
    beginMove() {
      const g = unwrap(graph.state.graph);
      const chosen = ctx.selected();
      ctx.setGesture({ type: "move", start: ctx.startPlaces(carried(g, chosen)), moved: chosen });
    },
    moveBy(dx, dy, free = false) {
      const gesture = ctx.gesture();
      if (gesture?.type !== "move") return;
      const start = gesture.start;
      let [sx, sy] = [dx, dy];
      if (!free) {
        const box = bounds(gesture.moved.map((id) => start.get(id)!).filter(Boolean));
        if (box) {
          const others = ctx.content().items.filter((i) => !start.has(i.id)).map((i) => i.place);
          const frames = ctx.content().frames.filter((f) => !start.has(f.id)).map((f) => f.place);
          const s = snap({ ...box, x: box.x + dx, y: box.y + dy }, [...others, ...frames], SNAP_PX / ctx.camera().k);
          [sx, sy] = [dx + s.dx, dy + s.dy];
          ctx.setGuides(s.guides);
        }
      } else ctx.setGuides([]);
      ctx.show(new Map([...start].map(([id, p]) => [id, { ...p, x: p.x + sx, y: p.y + sy }])), false);
    },
    beginResize(handle, ratio) {
      const start = ctx.startPlaces(ctx.selected());
      const box = bounds([...start.values()]);
      if (box) ctx.setGesture({ type: "resize", start, box, handle, ratio });
    },
    resizeBy(dx, dy, square = false) {
      const gesture = ctx.gesture();
      if (gesture?.type !== "resize") return;
      ctx.show(resized(gesture.start, gesture.box, gesture.handle, dx, dy, square, gesture.ratio ?? gesture.box.w / gesture.box.h), true);
    },
    beginRotate() {
      const start = ctx.startPlaces(ctx.selectedNodes());
      const box = bounds([...start.values()]);
      if (box) ctx.setGesture({ type: "rotate", start, centre: { x: box.x + box.w / 2, y: box.y + box.h / 2 } });
    },
    rotateTowards(p, stepped = false) {
      const gesture = ctx.gesture();
      if (gesture?.type !== "rotate") return;
      const angle = angleTo(gesture.centre, p, stepped);
      const { centre } = gesture;
      const turn = (angle * Math.PI) / 180;
      const one = gesture.start.size === 1;
      ctx.show(
        new Map(
          [...gesture.start].map(([id, place]) => {
            if (one) return [id, { ...place, rotate: angle }];
            // Several items turn around the selection's centre, each also turning itself.
            const [cx, cy] = [place.x + place.w / 2 - centre.x, place.y + place.h / 2 - centre.y];
            const [nx, ny] = [cx * Math.cos(turn) - cy * Math.sin(turn), cx * Math.sin(turn) + cy * Math.cos(turn)];
            return [id, { ...place, x: centre.x + nx - place.w / 2, y: centre.y + ny - place.h / 2, rotate: (place.rotate ?? 0) + angle }];
          }),
        ),
        false,
      );
    },
    async end() {
      const done = ctx.gesture();
      ctx.setGesture(null);
      ctx.setGuides([]);
      const places = ctx.live();
      project.dragBoxes({}, true, "board");
      if (!done || !places.size) return false;
      const changed = new Map([...places].filter(([id, p]) => JSON.stringify(rounded(p)) !== JSON.stringify(rounded(done.start.get(id) ?? p)) || !ctx.nodeOf(id)?.board));
      if (!changed.size) {
        ctx.setLive(new Map());
        return false;
      }
      const ok = await ctx.send(ctx.placeEdits(changed, done.type === "move" ? done.moved : []));
      // The graph has the places now (or kept the old ones): stop drawing the gesture's.
      ctx.setLive(new Map());
      return ok;
    },
    cancel() {
      if (!ctx.gesture()) return false;
      ctx.setGesture(null);
      ctx.setGuides([]);
      ctx.setLive(new Map());
      project.dragBoxes({}, true, "board");
      return true;
    },
    busy: ctx.busy,
  };
}
